import React, { FC, useMemo, useState } from 'react';
import styles from './styles.module.css';
import { AssetCard } from '../../../../clients/design_assets/AssetCard';
import { ShareModal } from '../../../../clients/design_assets/ShareModal';
import NavBar from '../NavBar';
import { DesignAsset } from '../../../../clients/design_assets/types';
import camelcaseKeys from 'camelcase-keys';
import useMediaQuery from '../../../../../clients/sidebar/views/CreateTask/create_task/useMediaQuery';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { Button, message } from 'antd';
import { Share } from 'lucide-react';
import { GroupNameModal } from '../../../../clients/design_assets/GroupNameModal';
import { put } from 'appJS/utils/fetch';
import usePusherSubscribe from 'appJS/hooks/usePusherSubscribe';
import { Event, Subscription } from 'appJS/models/Pusher';
import { handleSharingClick } from '../../../../clients/design_assets/handleSharingClick';
import { AntdWrapper } from '../../../../clients/shared/AntdWrapper';
import { Props as SharingProps } from '../../share_project/types';
import { ShareProject } from '../../share_project';
import {
  useOrganizationState,
  OrganizationProvider
} from '../../../../../clients/providers/Organization';
import DesignGroupNavigation, {
  DesignGroupNavigationAsset
} from '../DesignGroupNavigation';
import { sortDesignGroupAssets } from '../DesignGroupNavigation/utils';

type Props = {
  assets: DesignAsset[];
  viewKanbanProject: boolean;
  project: any;
  currentGroupName: string;
  bugherdUrl: string;
  groups: string[];
  pusherApiKey: string;
  pusherChannelAuthEndpoint: string;
  loggedIn: boolean;
  hasAccess: boolean;
  projectOwner: {
    email: string;
    name: string;
    id: number;
  } | null;
  sharingProps: SharingProps;
  isGuest: boolean;
};

const AssetGroup: FC<Props> = ({
  assets,
  viewKanbanProject,
  project,
  currentGroupName,
  bugherdUrl,
  groups,
  pusherApiKey,
  pusherChannelAuthEndpoint,
  loggedIn,
  projectOwner,
  hasAccess,
  sharingProps,
  isGuest
}) => {
  const [shareItem, setShareItem] = useState<number | string | null>(null);
  const [groupName, setGroupName] = useState<string>('');
  const [isGroupNameModalOpen, setIsGroupNameModalOpen] = useState<
    number | false
  >(false);
  const [data, setData] = useState<DesignAsset[]>(
    camelcaseKeys(assets, { deep: true })
  );
  const [groupNavigationCollapsed, setGroupNavigationCollapsed] = useState<
    boolean
  >(false);
  project = camelcaseKeys(project, { deep: true });
  const container = document.getElementById(
    'asset_group_container'
  ) as HTMLDivElement;
  const { id, pusherChannelName } = project;
  const shareGroupUrl = window.location.href;

  type PusherEventHandlerParams = {
    assetsClone: DesignAsset[];
    matchingGroup: boolean;
    pusherAsset: DesignAsset;
    existingAsset?: DesignAsset;
  };

  const handlePusherUpdate = (
    { asset },
    pusherEventHandler: ({
      assetsClone,
      matchingGroup,
      pusherAsset,
      existingAsset
    }: PusherEventHandlerParams) => DesignAsset[]
  ) => {
    const { id, group_name } = asset;

    const matchingGroup = group_name === currentGroupName;
    const existingAsset = data.find(_asset => _asset.id === id);
    const pusherAsset = camelcaseKeys(asset, { deep: true });

    setData(previousData => {
      const assetsClone = previousData.slice();
      return pusherEventHandler({
        assetsClone,
        matchingGroup,
        pusherAsset,
        existingAsset
      });
    });
  };

  const handleCreate = ({
    assetsClone,
    matchingGroup,
    pusherAsset,
    existingAsset
  }: PusherEventHandlerParams) =>
    !existingAsset && matchingGroup
      ? [...assetsClone, pusherAsset]
      : assetsClone;

  const handleUpdate = ({
    assetsClone,
    matchingGroup,
    pusherAsset,
    existingAsset
  }: PusherEventHandlerParams) => {
    if (existingAsset) {
      if (matchingGroup) {
        Object.assign(existingAsset, pusherAsset);
      } else {
        return assetsClone.filter(({ id }) => id !== existingAsset.id);
      }
    } else if (matchingGroup) return [...assetsClone, pusherAsset];

    return assetsClone;
  };

  const handleDelete = ({
    assetsClone,
    matchingGroup,
    existingAsset
  }: PusherEventHandlerParams) =>
    matchingGroup && existingAsset
      ? assetsClone.filter(({ id }) => id !== existingAsset.id)
      : assetsClone;

  const events = [
    {
      name: Event.ASSET_CREATE,
      onUpdate: pusherData => handlePusherUpdate(pusherData, handleCreate)
    },
    {
      name: Event.ASSET_UPDATE,
      onUpdate: pusherData => handlePusherUpdate(pusherData, handleUpdate)
    },
    {
      name: Event.ASSET_DELETE,
      onUpdate: pusherData => handlePusherUpdate(pusherData, handleDelete)
    }
  ];

  usePusherSubscribe({
    pusher: {
      config: {
        pusherApiKey,
        pusherChannelAuthEndpoint
      },
      projects: [{ id, pusherChannelName }],
      events,
      subscription: Subscription.ASSET_GROUP
    },
    loading: false
  });

  const buttonSize = useMediaQuery('(max-width: 560px)')
    ? 'small'
    : ('default' as SizeType);

  const onAddToGroup = async (groupName: string) => {
    if (groupName) {
      const response = await put(
        `${bugherdUrl}/projects/${project.id}/assets/${isGroupNameModalOpen}`,
        {
          group_name: groupName
        }
      );
      if (response.ok) {
        message.success(`Asset has been added to group ${groupName}`);
        setGroupName('');
      } else {
        message.error('Something went wrong, please try again');
      }
    }
  };

  const assetParameter =
    typeof shareItem === 'number'
      ? shareItem
      : `show_group?group_name=${encodeURI(currentGroupName)}`;

  const activeAssets = useMemo(
    () =>
      sortDesignGroupAssets(data.filter(asset => asset.status === 'created')),
    [data]
  );
  const { hasOrganizationExperiment } = useOrganizationState();
  const showGroupNavigation = activeAssets.length > 1;

  const onSharingClick = ({
    assetId,
    assetGroupName
  }: {
    assetId?: number | string;
    assetGroupName?: string;
  }) => {
    handleSharingClick({
      assetId,
      assetGroupName,
      callback: setShareItem
    });
  };

  const onSelectAsset = (asset: DesignGroupNavigationAsset) => {
    if (asset.shareToken) window.location.href = `/share/${asset.shareToken}`;
  };

  return (
    <div className={styles.container}>
      <ShareModal
        open={!!shareItem}
        closeModal={() => setShareItem(null)}
        shareURL={
          shareItem === 'number'
            ? `${bugherdUrl}/projects/${project.id}/assets/${assetParameter}`
            : shareGroupUrl
        }
        canInviteToProject={viewKanbanProject}
        container={container}
        inviteEndpoint={`/projects/${project.id}/kanban?invite_team=true`}
      />
      <ShareProject {...sharingProps} container={container} isGuest={isGuest} />
      <GroupNameModal
        onAddToGroup={onAddToGroup}
        setIsGroupNameModalOpen={setIsGroupNameModalOpen}
        setGroupName={setGroupName}
        isGroupNameModalOpen={isGroupNameModalOpen}
        container={container}
        groupName={groupName}
      />
      <div className={styles.pageHeader}>
        <NavBar
          project={project}
          fileName={currentGroupName}
          viewKanbanProject={viewKanbanProject}
          loggedIn={loggedIn}
          hasAccess={hasAccess}
        />
        {loggedIn && (
          <Button
            size={buttonSize}
            title="Share this asset group"
            className={styles.shareButton}
            icon={<Share className={styles.shareIcon} />}
            onClick={() => onSharingClick({ assetGroupName: currentGroupName })}
          >
            Share
          </Button>
        )}
      </div>
      <div className={styles.content}>
        {showGroupNavigation && (
          <DesignGroupNavigation
            assets={activeAssets}
            collapsed={groupNavigationCollapsed}
            onSelect={onSelectAsset}
            onToggle={() =>
              setGroupNavigationCollapsed(collapsed => !collapsed)
            }
          />
        )}
        <div className={styles.assetsContainer}>
          {activeAssets.map(asset => (
            <AssetCard
              asset={camelcaseKeys(asset, { deep: true })}
              key={asset.id}
              container={container}
              canManageAssets={viewKanbanProject}
              disabled={asset.status === 'archived'}
              setData={setData}
              bugherdUrl={bugherdUrl}
              handleSharingClick={() => onSharingClick({ assetId: asset.id })}
              onAddToGroup={onAddToGroup}
              openNewGroupModal={() => setIsGroupNameModalOpen(asset.id)}
              existingGroups={groups}
              isGuest={isGuest}
              loggedIn={loggedIn}
              useShareLink
              shareToken={asset.shareToken}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default (props: Props) => (
  <AntdWrapper>
    <OrganizationProvider id={props.project.organization_id}>
      <AssetGroup {...props} />
    </OrganizationProvider>
  </AntdWrapper>
);
